import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PAGES, ROLE_VIEWS, ROLE_NAMES, viewFor, canAccess } from "@/config/roleViews";
import { api } from "@/lib/api";
import { useLiveSync, useLiveRefresh } from "@/lib/live";
import ScanDialog from "@/components/ScanDialog";
import {
  LayoutDashboard,
  BellRing,
  ScanLine,
  ClipboardList,
  ClipboardCheck,
  Factory,
  Boxes,
  Cog,
  UserCog,
  FileBarChart,
  Users as UsersIcon,
  Upload,
  LogOut,
  Printer,
  Receipt,
  TrendingDown,
  Building2,
  Wrench,
  Eye,
  LayoutGrid,
  Menu,
  X,
} from "lucide-react";

// Icons per page; WHO sees WHAT lives in config/roleViews.js
const ICONS = {
  "/": LayoutDashboard, "/daily": BellRing, "/orders": ClipboardList, "/production": Factory, "/avt": ClipboardCheck, "/inventory": Boxes,
  "/machines": Cog, "/equipment": Wrench, "/operators": UserCog, "/sales": Receipt,
  "/purchases": TrendingDown, "/customers": Building2, "/reports": FileBarChart,
  "/users": UsersIcon, "/import": Upload, "/views": LayoutGrid,
};

// Short labels for the phone bottom bar
const SHORT = { "/": "Home", "/daily": "Daily", "/orders": "Jobs", "/production": "Produce", "/avt": "AVT", "/inventory": "Stock", "/operators": "People",
  "/machines": "Machines", "/equipment": "Equip.", "/purchases": "Buy", "/customers": "Clients", "/import": "Import", "/views": "Views" };

function Brand() {
  return (
    <div className="flex items-center gap-2">
      <div className="w-8 h-8 bg-[#FF5A00] flex items-center justify-center">
        <Printer size={18} strokeWidth={2} className="text-white" />
      </div>
      <div>
        <div className="font-display font-black text-sm tracking-tight leading-none">MEDPACK</div>
        <div className="label-caps mt-1 text-[10px]">ERP · v1</div>
      </div>
    </div>
  );
}

// Sidebar contents — shared by the desktop sidebar and the phone drawer
function SidebarBody({ navItems, onNavigate, onScan, alertCount }) {
  const { user, realUser, previewing, setViewAs, logout } = useAuth();
  const view = viewFor(user);
  const nav = useNavigate();
  return (
    <>
      <div className="px-3 pt-3">
        <button type="button" onClick={() => { onScan && onScan(); onNavigate && onNavigate(); }} data-testid="scan-btn"
          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm border border-neutral-200 text-neutral-700 hover:border-[#FF5A00] hover:text-[#FF5A00] transition-colors">
          <ScanLine size={16} strokeWidth={1.75} /> Scan a job sheet
        </button>
      </div>
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {navItems.map((to) => {
          const Icon = ICONS[to];
          return (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              onClick={onNavigate}
              data-testid={`nav-${to.replace("/", "") || "dashboard"}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors duration-150 border-l-2 ${
                  isActive
                    ? "border-[#FF5A00] bg-neutral-50 text-neutral-900 font-medium"
                    : "border-transparent text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900"
                }`
              }
            >
              <Icon size={16} strokeWidth={1.75} />
              {view.navLabels?.[to] || PAGES[to]}
              {to === "/daily" && alertCount > 0 && (
                <span className="ml-auto min-w-5 px-1.5 text-center text-[10px] font-semibold leading-5 bg-rose-600 text-white rounded-full" data-testid="alerts-badge">
                  {alertCount > 999 ? "999+" : alertCount}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-3 border-t border-neutral-200">
        <div className="px-2 py-2">
          <div className="text-xs font-medium text-neutral-900 truncate">{user?.name}</div>
          <div className="label-caps mt-0.5 text-[10px]" data-testid="role-label">{view.label}</div>
        </div>
        {realUser?.role === "admin" && (
          <label className="block px-2 pb-2">
            <span className="label-caps text-[10px] flex items-center gap-1"><Eye size={11} /> View as</span>
            <select
              data-testid="view-as-select"
              value={previewing ? user.role : "admin"}
              onChange={(e) => { setViewAs(e.target.value === "admin" ? null : e.target.value); nav("/"); onNavigate && onNavigate(); }}
              className="mt-1 w-full text-xs border border-neutral-200 bg-white px-2 py-1.5 rounded-sm"
            >
              {ROLE_NAMES.map((r) => <option key={r} value={r}>{ROLE_VIEWS[r].label}</option>)}
            </select>
          </label>
        )}
        <button
          data-testid="logout-btn"
          onClick={() => { logout(); nav("/login"); }}
          className="mt-2 w-full flex items-center gap-2 px-3 py-2 text-sm border border-neutral-200 hover:bg-neutral-50 transition-colors rounded-sm"
        >
          <LogOut size={14} strokeWidth={1.75} /> Sign out
        </button>
      </div>
    </>
  );
}

export default function Layout() {
  useLiveSync();
  const { user, previewing, setViewAs } = useAuth();
  const view = viewFor(user);
  const navItems = view.nav.filter((p) => canAccess(user, p));
  const [alertCount, setAlertCount] = useState(0);
  const hasAlerts = navItems.includes("/daily");
  const loadAlerts = () => { api.get("/dashboard/daily/count").then(({ data }) => setAlertCount(data.count || 0)).catch(() => {}); };
  useEffect(() => { if (hasAlerts) loadAlerts(); }, [hasAlerts]); // eslint-disable-line react-hooks/exhaustive-deps
  useLiveRefresh("jobs", loadAlerts, { enabled: hasAlerts });
  const nav = useNavigate();
  const loc = useLocation();
  const [drawer, setDrawer] = useState(false);
  const [scanning, setScanning] = useState(false);
  useEffect(() => { setDrawer(false); }, [loc.pathname]);

  // Phone bottom bar: the first 4 pages of the view + a Menu button for the rest
  const bottom = navItems.slice(0, 4);

  return (
    <div className="flex min-h-screen bg-[#F7F7F8]">
      {/* Desktop / tablet sidebar */}
      <aside className="hidden md:flex w-60 shrink-0 border-r border-neutral-200 bg-white flex-col" data-testid="sidebar">
        <div className="px-5 py-6 border-b border-neutral-200"><Brand /></div>
        <SidebarBody navItems={navItems} onScan={() => setScanning(true)} alertCount={alertCount} />
      </aside>

      {/* Phone drawer */}
      {drawer && (
        <div className="md:hidden fixed inset-0 z-50 flex" data-testid="mobile-drawer">
          <div className="w-72 max-w-[85vw] bg-white flex flex-col h-full shadow-xl">
            <div className="px-4 py-4 border-b border-neutral-200 flex items-center justify-between">
              <Brand />
              <button aria-label="Close menu" onClick={() => setDrawer(false)} className="p-2 -mr-2"><X size={20} /></button>
            </div>
            <SidebarBody navItems={navItems} onNavigate={() => setDrawer(false)} onScan={() => setScanning(true)} alertCount={alertCount} />
          </div>
          <div className="flex-1 bg-black/40" onClick={() => setDrawer(false)} />
        </div>
      )}

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col pb-16 md:pb-0">
        {/* Phone top bar */}
        <div className="md:hidden sticky top-0 z-30 bg-white border-b border-neutral-200 px-3 py-2.5 flex items-center justify-between" data-testid="mobile-topbar">
          <button aria-label="Open menu" data-testid="mobile-menu-btn" onClick={() => setDrawer(true)} className="p-2 -ml-2"><Menu size={22} /></button>
          <Brand />
          <button aria-label="Scan a job sheet" data-testid="scan-btn-mobile" onClick={() => setScanning(true)} className="p-2 -mr-2"><ScanLine size={22} /></button>
        </div>

        {previewing && (
          <div data-testid="preview-banner" className="bg-amber-100 border-b border-amber-300 text-amber-900 text-xs px-4 py-2 flex items-center justify-between gap-3">
            <span>Previewing the <b>{view.label}</b> view. Display only — the server still applies your admin rights.</span>
            <button onClick={() => { setViewAs(null); nav("/"); }} className="underline font-semibold shrink-0">Exit preview</button>
          </div>
        )}
        <Outlet />
      </main>
      <ScanDialog open={scanning} onOpenChange={setScanning} />

      {/* Phone bottom navigation */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-neutral-200 grid"
        style={{ gridTemplateColumns: `repeat(${bottom.length + 1}, minmax(0, 1fr))`, paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="mobile-bottom-nav"
      >
        {bottom.map((to) => {
          const Icon = ICONS[to];
          return (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] ${isActive ? "text-[#FF5A00] font-semibold" : "text-neutral-500"}`
              }
            >
              <Icon size={20} strokeWidth={1.75} />
              {SHORT[to] || PAGES[to]}
            </NavLink>
          );
        })}
        <button onClick={() => setDrawer(true)} className="flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] text-neutral-500">
          <Menu size={20} strokeWidth={1.75} />
          Menu
        </button>
      </nav>
    </div>
  );
}
